import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { STORAGE_KEYS } from '@constants';
import { listMyCompany } from '@/features/companies/companySlice';
import { fetchSalesTeamMyDistributors } from '@/features/tallyReports/tallyReportsSlice';
import { isOnboardingComplete, getCurrentUserFromStorage } from '@/utils/onboardingUtils';
import { clearSession, expireSession, getTokenExpiry, isTokenExpired } from '@/utils/session';

const ProtectedRoute = ({ children }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [onboardingDone, setOnboardingDone] = useState(true);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth?.user);
  const myCompanyMeta = useSelector((state) => state.companies?.myCompanyMeta);
  const isCompanyRoleOwner =
    user?.companyRole?.is_owner === true ||
    user?.company_role?.is_owner === true ||
    user?.company_role_is_owner === true;

  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (token && isTokenExpired(token)) {
          clearSession();
        }
        const authed = !!token && !isTokenExpired(token);
        setIsAuthenticated(authed);

        if (authed) {
          const storedUser = getCurrentUserFromStorage();
          setOnboardingDone(isOnboardingComplete(storedUser));
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        setOnboardingDone(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Log out as soon as the token expires, even if the user is idle and makes no requests.
  useEffect(() => {
    if (!isAuthenticated) return;
    const expiry = getTokenExpiry(localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN));
    if (!expiry) return;
    // setTimeout overflows above ~24.8 days; long-lived tokens are caught by the 401 handler instead.
    const delay = expiry - Date.now();
    if (delay > 2147483647) return;
    const timer = setTimeout(expireSession, Math.max(delay, 0));
    return () => clearTimeout(timer);
  }, [isAuthenticated]);

  useEffect(() => {
    if (user) {
      setOnboardingDone(isOnboardingComplete(user));
    }
  }, [user]);

  // Fetch myCompany once we know the user is authenticated and user id is available.
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!user?.id) return;
    if (myCompanyMeta?.lastFetchedAt || myCompanyMeta?.isFetching) return;

    // Dispatch listMyCompany to populate companies.myCompany early for pages that depend on it.
    dispatch(listMyCompany({ owner_user_id: user.id }));
  }, [isAuthenticated, user?.id, myCompanyMeta?.lastFetchedAt, myCompanyMeta?.isFetching, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    dispatch(fetchSalesTeamMyDistributors({ bypassForOwner: isCompanyRoleOwner }));
  }, [isAuthenticated, user?.id, isCompanyRoleOwner, dispatch]);

  if (isChecking) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!onboardingDone) {
    return <Navigate to="/onboarding/role" replace />;
  }

  return children;
};

export default ProtectedRoute;

